import { forwardRef, useImperativeHandle, useEffect, useState } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

let scriptPromise = null;
function loadRecaptcha() {
  if (!SITE_KEY) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) return resolve();
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // ready() waits until grecaptcha internals are hydrated
      window.grecaptcha.ready(() => resolve());
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Invisible reCAPTCHA v3 wrapper.
 * Parent calls ref.getValue(action) at submit time to fetch a fresh token.
 */
const Captcha = forwardRef(function Captcha({ action = "submit", testid = "captcha" }, ref) {
  const [error, setError] = useState("");
  useEffect(() => {
    loadRecaptcha().catch(() => setError("Captcha script failed to load"));
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: async (timeoutMs = 5000) => {
      if (!SITE_KEY) return "dev";
      try {
        await loadRecaptcha();
        // Race the token fetch against a timeout — grecaptcha.execute can hang
        // silently when the current domain isn't registered against the site
        // key (common on preview URLs). We return "" so the backend's bypass
        // list can decide whether to accept the submission.
        return await Promise.race([
          window.grecaptcha.execute(SITE_KEY, { action }),
          new Promise((_, rej) => setTimeout(() => rej(new Error("captcha timeout")), timeoutMs)),
        ]);
      } catch (e) {
        setError("Captcha couldn't verify this domain — submitting anyway.");
        return "";
      }
    },
    reset: () => {},
  }));

  return (
    <div className="text-[10px] text-slate-400 flex items-center gap-1.5" data-testid={testid}>
      {error ? (
        <span className="text-rose-600">{error}</span>
      ) : SITE_KEY ? (
        <>
          <span>Protected by reCAPTCHA · </span>
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="hover:text-slate-600 underline">Privacy</a>
          <span>·</span>
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="hover:text-slate-600 underline">Terms</a>
        </>
      ) : (
        <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
          reCAPTCHA site key missing — bypassed in dev.
        </span>
      )}
    </div>
  );
});

export default Captcha;
